import {
  Block,
  Image as SanityImage,
  Reference as SanityReference,
} from '@sanity/types';
import {
  Auth0ThemePosition,
  Auth0ThemeBackgroundType,
} from '~/types/authTheme';

export type TSanityBlockContent = Block[];
export type TSanityTypedObject<
  T extends Record<string, any> = Record<string, any>,
> = { _type: string } & {
  [P in keyof T]: T[P];
};

export enum SanityObjectSchema {
  AUTH_THEME = 'authTheme',
  BLOCK_CONTENT_WITH_TITLE = 'blockContentWithTitle',
  CAST_MEMBER = 'castMember',
  CHAT_CONFIG = 'chatConfig',
  CONTENT_AVAILABILITY = 'contentAvailability',
  CONTENT_RATING = 'contentRating',
  CREW_MEMBER = 'crewMember',
  CTA = 'cta',
  CTA_WITH_REFLINK = 'ctaWithReflink',
  IMAGE_WITH_ALT_TEXT = 'imageWithAltText',
  PAGE_AVAILABILITY = 'pageAvailability',
  PROJECT_TIMELINE = 'projectTimeline',
  PROMO_BOX_CONTENT = 'promoBoxContent',
  PROMO_CARD = 'promoCard',
  PROMO_CAROUSEL_ITEM = 'promoCarouselItem',
  REDIRECT_TO = 'redirectTo',
  RELATIVE_URL_OR_REFLINK = 'relativeUrlOrReflink',
  REWARD_ITEM = 'rewardItem',
  ROW_HERO_VIDEO = 'rowHeroVideo',
  ROW_WITH_COLUMNS = 'rowWithColumns',
  ROW_TEXT_WITH_IMAGE = 'rowTextWithImage',
  RUN_TIME = 'runTime',
  SEO = 'seo',
  TIMELINE_PERIOD = 'timelinePeriod',
  TRANSCRIPT = 'transcript',
  URL_OR_REFLINK = 'urlOrReflink',
  UTM_CONTENT = 'utmContent',
}

export interface ISanityObjectsSchemaInterfaceMap {
  [SanityObjectSchema.AUTH_THEME]: IAuthTheme;
  [SanityObjectSchema.BLOCK_CONTENT_WITH_TITLE]: IBlockContentWithTitle;
  [SanityObjectSchema.CAST_MEMBER]: ICastMember;
  [SanityObjectSchema.CHAT_CONFIG]: IChatConfig;
  [SanityObjectSchema.CONTENT_AVAILABILITY]: IContentAvailability;
  [SanityObjectSchema.CONTENT_RATING]: IContentRating;
  [SanityObjectSchema.CREW_MEMBER]: ICrewMember;
  [SanityObjectSchema.CTA]: ICta;
  [SanityObjectSchema.CTA_WITH_REFLINK]: ICtaWithReflink;
  [SanityObjectSchema.IMAGE_WITH_ALT_TEXT]: IImageWithAltText;
  [SanityObjectSchema.PAGE_AVAILABILITY]: IPageAvailability;
  [SanityObjectSchema.PROJECT_TIMELINE]: IProjectTimeline;
  [SanityObjectSchema.PROMO_BOX_CONTENT]: IPromoBoxContent;
  [SanityObjectSchema.PROMO_CAROUSEL_ITEM]: IPromoCarouselItem;
  [SanityObjectSchema.PROMO_CARD]: IPromoCard;
  [SanityObjectSchema.REDIRECT_TO]: IRedirectTo;
  [SanityObjectSchema.RELATIVE_URL_OR_REFLINK]: IRedirectTo;
  [SanityObjectSchema.REWARD_ITEM]: IRewardItem;
  [SanityObjectSchema.ROW_HERO_VIDEO]: IRowHeroVideo;
  [SanityObjectSchema.ROW_TEXT_WITH_IMAGE]: IRowTextWithImage;
  [SanityObjectSchema.ROW_WITH_COLUMNS]: IRowWithColumns;
  [SanityObjectSchema.RUN_TIME]: IVideoTime;
  [SanityObjectSchema.SEO]: ISeo;
  [SanityObjectSchema.TIMELINE_PERIOD]: ITimelinePeriod;
  [SanityObjectSchema.TRANSCRIPT]: ITranscript;
  [SanityObjectSchema.URL_OR_REFLINK]: IUrlOrReflink;
  [SanityObjectSchema.UTM_CONTENT]: IUtmContent;
}

export type ISanityObjects = {
  [K in SanityObjectSchema]: TSanityTypedObject<
    ISanityObjectsSchemaInterfaceMap[K] & { _type: K }
  >;
};

export interface IAuthTheme {
  backgroundType?: Auth0ThemeBackgroundType;
  gradientColor1?: IColor;
  gradientColor2?: IColor;
  imageBackground?: SanityImage;
  linkColor?: IColor;
  loginPosition?: Auth0ThemePosition;
  overlay?: boolean;
  title?: string;
  titleColor?: IColor;
  videoBackground?: string;
}

export interface IBlockContentWithTitle {
  body?: TSanityBlockContent;
  title?: TSanityBlockContent;
}

export interface ICastMember {
  characterName?: string;
  person?: SanityReference;
}

export interface IChatConfig {
  isEnabled?: boolean;
  isMessagingEnabled?: boolean;
}

export interface IColor {
  _type: 'color';
  hex: string;
  alpha?: number;
  hsl?: {
    _type: 'hslaColor';
    h: number;
    s: number;
    l: number;
    a: number;
  };
  hsv?: {
    _type: 'hsvaColor';
    h: number;
    s: number;
    v: number;
    a: number;
  };
  rgb?: {
    _type: 'rgbaColor';
    r: number;
    g: number;
    b: number;
    a: number;
  };
}

export interface IContentAvailability {
  isBuyable?: boolean;
  isRentable?: boolean;
  isWatchable?: boolean;
}

export interface IContentRating {
  rating?: SanityReference;
  ratingMinAge?: number;
  ratingDetails?: string;
}

export interface ICrewMember {
  department?: SanityReference;
  person?: SanityReference;
  role?: SanityReference;
}

export interface ICta {
  isHidden?: boolean;
  text?: string;
  url?: string;
}

export interface ICtaWithReflink extends ICta {
  linkToDocumentRef?: boolean;
}

export interface IImageWithAltText extends SanityImage {
  alt?: string;
}

export interface IPageAvailability {
  isLandingPageEnabled?: boolean;
  isUaPageEnabled?: boolean;
  isCreateAccountPageEnabled?: boolean;
}

export interface IProjectTimeline {
  timeline?: SanityReference;
  currentStep?: number;
}

export interface IPromoCarouselItem {
  badges?: SanityReference[];
  cta?: ISanityObjects[SanityObjectSchema.CTA_WITH_REFLINK];
  documentReference?: SanityReference;
  endsAt?: string;
  startsAt?: string;
}

export interface IPromoCard {
  heroImageLandscape?: SanityImage;
  heroImagePortrait?: SanityImage;
  logo?: IImageWithAltText;
  text?: string;
}

export interface IPromoBoxContent {
  body?: TSanityBlockContent;
  overline?: string;
  title?: string;
}

export interface IRedirectTo {
  documentReference?: SanityReference;
  href?: string;
  linkType?: 'url' | 'docRef';
  reflinkSuffix?: string;
}

export interface IUrlOrReflink extends IRedirectTo {
  blank?: boolean;
}

export interface IRewardItem {
  count?: number;
  description?: TSanityBlockContent;
  title?: string;
}

export interface IRowHeroVideo {
  body?: TSanityBlockContent;
  ctaText?: string;
  heroImage?: SanityImage;
  title?: TSanityBlockContent;
  video?: SanityReference;
}

export interface IRowWithColumns {
  backgroundColor?: TThemeColorToken;
  body?: Array<{
    _key?: string;
    backgroundColor?: TThemeColorToken;
    backgroundColorIncludesGutters?: boolean;
    body?: TSanityBlockContent;
    textStyles?: IColTextFormatting;
    vColOptions?: IVColOptions;
  }>;
  displayOptions?: {
    hasContentContainer?: boolean;
    hasPaddingBottom?: boolean;
    hasPaddingTop?: boolean;
  };
  title?: TSanityBlockContent;
  vRowOptions?: IVRowOptions;
}

interface IRowBpOptions {
  align?: string;
  alignContent?: string;
  justify?: string;
}

interface ISeo {
  description?: string;
  ogImage?: IImageWithAltText;
  ogDescription?: string;
  ogTitle?: string;
  title?: string;
}

interface IUtmContent {
  keyValuePairs?: Array<{ key?: string; value?: string }>;
}

export type TBreakpoints = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface IVRowOptions {
  bpOptions?: Partial<Record<TBreakpoints, IRowBpOptions>>;
  gutters?: 'no-gutters' | 'dense';
}

interface IColBpOptions {
  cols?: number;
  offset?: number;
  order?: number;
}

export interface IVColOptions {
  alignSelf?: string;
  bpOptions?: Partial<Record<TBreakpoints, IColBpOptions>>;
}

interface IColTextBpOptions {
  textAlign?: 'left' | 'center' | 'right';
}

export interface ITypography {
  fontStyle?: string;
  fontSize?: 'small' | 'medium' | 'large';
  color?: TThemeColorToken;
}
export interface IColTextFormatting {
  typography?: ITypography;
  bpOptions?: Partial<Record<TBreakpoints, IColTextBpOptions>>;
}

export interface IRowTextWithImage {
  body?: IBlockContentWithTitle[];
  displayOptions?: {
    isColumnLayoutReversed?: boolean;
    isHeroImage?: boolean;
    isRowLayoutReversed?: boolean;
  };
  image?: IImageWithAltText;
  title?: TSanityBlockContent;
}

export interface IVideoTime {
  hours?: number;
  minutes?: number;
  seconds?: number;
}

export interface ITimelinePeriod {
  period?: SanityReference;
  description?: TSanityBlockContent;
}

export interface ITranscript {
  language?: SanityReference;
  type?: SanityReference;
}

export type TThemeColorToken =
  | 'accent'
  | 'accent-muted'
  | 'primary'
  | 'secondary'
  | 'tertiary';

export interface IObjectPosition {
  x?: 'left' | 'center' | 'right';
  y?: 'top' | 'center' | 'bottom';
}

export interface ISanityImageWithObjectPosition extends SanityImage {
  objectPosition?: IObjectPosition;
}
